import React, { ElementType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import { ArrowUpRight, Building2, Globe, Mail, Phone, Plus, Users } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteCompany, getCompanies } from '../hooks/companies';
import { Company } from '../utils/types';
import {
  CompanyCommandBar,
  CompanyDashboardSkeleton,
  CompanyEmptyState,
  CompanyHero,
  CompanyMetricStrip,
  CompanyPrimaryButton,
  CompanyShell,
  CompanyStatusBadge,
  companyAccent,
  companyBorder,
} from './companies/components/companyUi';

const normalizeCompanies = (data: unknown): Company[] => {
  if (Array.isArray(data)) {
    return data as Company[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Company[] }).results;
  }

  return [];
};

const hasContactDetails = (company: Company) => Boolean(company.email || company.phone);
const isCompleteCompany = (company: Company) => Boolean(company.website && hasContactDetails(company));
const getDisplayDomain = (website?: string) => {
  if (!website) {
    return 'No website';
  }

  return website.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

const getPrimaryChannel = (company: Company) => {
  if (company.email) {
    return company.email;
  }

  if (company.phone) {
    return company.phone;
  }

  return 'No contact channel';
};

const sortCompanies = (companies: Company[], sort: string) => {
  const next = [...companies];

  switch (sort) {
    case 'name-desc':
      return next.sort((a, b) => b.name.localeCompare(a.name));
    case 'website-first':
      return next.sort((a, b) => Number(Boolean(b.website)) - Number(Boolean(a.website)));
    case 'contact-first':
      return next.sort(
        (a, b) => Number(hasContactDetails(b)) - Number(hasContactDetails(a)),
      );
    case 'name-asc':
    default:
      return next.sort((a, b) => a.name.localeCompare(b.name));
  }
};

type CompanyRowProps = {
  company: Company;
  onOpen: (companyId: string) => void;
  onEdit: (companyId: string) => void;
  onDelete: (company: Company) => void;
};

const CompanyTableRow = ({ company, onDelete, onEdit, onOpen }: CompanyRowProps) => (
  <Tr _hover={{ bg: 'gray.50' }} transition="background 0.16s ease">
    <Td py={4}>
      <HStack spacing={4} align="center">
        <Avatar
          size="sm"
          name={company.name}
          bg={companyAccent}
          color="white"
        />
        <VStack align="flex-start" spacing={1}>
          <Text
            fontWeight="800"
            color="gray.900"
            cursor="pointer"
            onClick={() => onOpen(company.id)}
          >
            {company.name}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {getDisplayDomain(company.website)}
          </Text>
        </VStack>
      </HStack>
    </Td>
    <Td py={4}>
      <CompanyStatusBadge complete={isCompleteCompany(company)} />
    </Td>
    <Td py={4}>
      <Text color="gray.700">{getPrimaryChannel(company)}</Text>
    </Td>
    <Td py={4}>
      <HStack spacing={2}>
        {company.website ? (
          <Badge borderRadius="full" px={3} py={1} colorScheme="teal">
            Website
          </Badge>
        ) : null}
        {company.email ? (
          <Badge borderRadius="full" px={3} py={1}>
            Email
          </Badge>
        ) : null}
        {company.phone ? (
          <Badge borderRadius="full" px={3} py={1} colorScheme="orange">
            Phone
          </Badge>
        ) : null}
        {!company.website && !company.email && !company.phone ? (
          <Badge borderRadius="full" px={3} py={1} colorScheme="red">
            Needs data
          </Badge>
        ) : null}
      </HStack>
    </Td>
    <Td py={4}>
      <HStack justify="flex-end" spacing={1}>
        <Tooltip label="Open company" hasArrow>
          <IconButton
            aria-label="Open company"
            icon={<ViewIcon />}
            variant="ghost"
            onClick={() => onOpen(company.id)}
          />
        </Tooltip>
        <Tooltip label="Edit company" hasArrow>
          <IconButton
            aria-label="Edit company"
            icon={<EditIcon />}
            variant="ghost"
            onClick={() => onEdit(company.id)}
          />
        </Tooltip>
        <Tooltip label="Delete company" hasArrow>
          <IconButton
            aria-label="Delete company"
            icon={<DeleteIcon />}
            variant="ghost"
            colorScheme="red"
            onClick={() => onDelete(company)}
          />
        </Tooltip>
      </HStack>
    </Td>
  </Tr>
);

const CompanyCard = ({ company, onDelete, onEdit, onOpen }: CompanyRowProps) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor={companyBorder}
    borderRadius="24px"
    overflow="hidden"
    boxShadow="0 14px 36px rgba(15, 23, 42, 0.05)"
  >
    <Stack spacing={4} p={4}>
      <HStack justify="space-between" align="flex-start">
        <HStack spacing={3} align="flex-start">
          <Avatar size="sm" name={company.name} bg={companyAccent} color="white" />
          <VStack align="flex-start" spacing={1}>
            <Text
              fontWeight="800"
              fontSize="lg"
              color="gray.900"
              cursor="pointer"
              onClick={() => onOpen(company.id)}
            >
              {company.name}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {getDisplayDomain(company.website)}
            </Text>
          </VStack>
        </HStack>
        <CompanyStatusBadge complete={isCompleteCompany(company)} />
      </HStack>

      <Stack spacing={3}>
        <HStack spacing={2} color="gray.600">
          <Box as={Mail as ElementType} boxSize={4} />
          <Text fontSize="sm">{company.email || 'No email address'}</Text>
        </HStack>
        <HStack spacing={2} color="gray.600">
          <Box as={Phone as ElementType} boxSize={4} />
          <Text fontSize="sm">{company.phone || 'No phone number'}</Text>
        </HStack>
        <HStack spacing={2} color="gray.600">
          <Box as={Globe as ElementType} boxSize={4} />
          <Text fontSize="sm">{getDisplayDomain(company.website)}</Text>
        </HStack>
      </Stack>

      <HStack spacing={2} flexWrap="wrap">
        {company.website ? (
          <Badge borderRadius="full" px={3} py={1} colorScheme="teal">
            Website
          </Badge>
        ) : (
          <Badge borderRadius="full" px={3} py={1} colorScheme="red">
            Missing website
          </Badge>
        )}
        {hasContactDetails(company) ? (
          <Badge borderRadius="full" px={3} py={1} colorScheme="green">
            Contact ready
          </Badge>
        ) : (
          <Badge borderRadius="full" px={3} py={1} colorScheme="orange">
            Missing contact
          </Badge>
        )}
      </HStack>

      <HStack spacing={2}>
        <Button
          flex="1"
          borderRadius="16px"
          rightIcon={<Box as={ArrowUpRight as ElementType} />}
          onClick={() => onOpen(company.id)}
        >
          Open
        </Button>
        <IconButton
          aria-label="Edit company"
          icon={<EditIcon />}
          borderRadius="16px"
          onClick={() => onEdit(company.id)}
        />
        <IconButton
          aria-label="Delete company"
          icon={<DeleteIcon />}
          borderRadius="16px"
          variant="ghost"
          colorScheme="red"
          onClick={() => onDelete(company)}
        />
      </HStack>
    </Stack>
  </Box>
);

export default function CompanyManagement() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const searchRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('name-asc');
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCompaniesList = async () => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    const response = await getCompanies(orgId);
    setLoading(false);

    if (!response.success) {
      toast({
        title: 'Could not load companies',
        description: 'Please refresh and try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setCompanies(normalizeCompanies(response.data));
  };

  useEffect(() => {
    fetchCompaniesList();
  }, [orgId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && document.activeElement !== searchRef.current) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const filteredCompanies = useMemo(() => {
    const query = search.trim().toLowerCase();

    const next = companies.filter((company) => {
      const matchesSearch =
        !query ||
        company.name.toLowerCase().includes(query) ||
        company.website?.toLowerCase().includes(query) ||
        company.email?.toLowerCase().includes(query) ||
        company.phone?.toLowerCase().includes(query);

      const matchesFilter =
        filter === 'all' ||
        (filter === 'complete' && isCompleteCompany(company)) ||
        (filter === 'missing-contact' && !hasContactDetails(company)) ||
        (filter === 'missing-website' && !company.website);

      return matchesSearch && matchesFilter;
    });

    return sortCompanies(next, sort);
  }, [companies, filter, search, sort]);

  const metrics = useMemo(
    () => [
      {
        label: 'Companies',
        value: String(companies.length),
        hint: 'All company records',
        active: filter === 'all',
        onClick: () => setFilter('all'),
      },
      {
        label: 'Complete',
        value: String(companies.filter((company) => isCompleteCompany(company)).length),
        hint: 'Website plus contact',
        active: filter === 'complete',
        onClick: () => setFilter('complete'),
      },
      {
        label: 'Missing contact',
        value: String(companies.filter((company) => !hasContactDetails(company)).length),
        hint: 'Needs email or phone',
        active: filter === 'missing-contact',
        onClick: () => setFilter('missing-contact'),
      },
      {
        label: 'Missing website',
        value: String(companies.filter((company) => !company.website).length),
        hint: 'Lower recognition quality',
        active: filter === 'missing-website',
        onClick: () => setFilter('missing-website'),
      },
    ],
    [companies, filter],
  );

  const openCompany = (companyId: string) => {
    navigate(`/organization/${orgId}/manage-companies/${companyId}`);
  };

  const editCompanyRecord = (companyId: string) => {
    navigate(`/organization/${orgId}/manage-companies/${companyId}/edit`);
  };

  const createCompany = () => {
    navigate(`/organization/${orgId}/manage-companies/new`);
  };

  const confirmDelete = (company: Company) => {
    setDeleteTarget(company);
    onOpen();
  };

  const handleDelete = async () => {
    if (!orgId || !deleteTarget) {
      return;
    }

    setDeleting(true);
    const response = await deleteCompany(orgId, deleteTarget.id);
    setDeleting(false);

    if (!response.success) {
      toast({
        title: 'Could not delete company',
        description: 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setCompanies((current) => current.filter((company) => company.id !== deleteTarget.id));
    toast({
      title: 'Company deleted',
      description: `${deleteTarget.name} was removed successfully.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    setDeleteTarget(null);
    onClose();
  };

  return (
    <CompanyShell>
      <CompanyHero
        eyebrow="Contact companies"
        title="A cleaner company workspace for browsing, qualification, and follow-up"
        description="Search, assess, and manage company records with better visibility into profile quality and the next action your team should take."
        primaryAction={
          <CompanyPrimaryButton
            leftIcon={<Plus size={16} />}
            onClick={createCompany}
          >
            Add company
          </CompanyPrimaryButton>
        }
        secondaryAction={
          <Button borderRadius="16px" variant="outline" onClick={fetchCompaniesList}>
            Refresh
          </Button>
        }
      />

      <CompanyMetricStrip items={metrics} />

      <CompanyCommandBar
        search={search}
        onSearchChange={setSearch}
        inputRef={searchRef}
        filter={filter}
        onFilterChange={setFilter}
        sort={sort}
        onSortChange={setSort}
        view={view}
        onViewChange={setView}
        resultText={`${filteredCompanies.length} of ${companies.length} companies shown`}
      />

      {loading ? (
        <CompanyDashboardSkeleton />
      ) : filteredCompanies.length === 0 ? (
        <CompanyEmptyState
          title={companies.length ? 'No companies match these filters' : 'No companies yet'}
          description={
            companies.length
              ? 'Try clearing search or switching filters to widen the result set.'
              : 'Start building your company workspace with profiles your team can reuse across leads and CRM flows.'
          }
          action={
            <CompanyPrimaryButton
              leftIcon={<Plus size={16} />}
              onClick={createCompany}
            >
              Add company
            </CompanyPrimaryButton>
          }
        />
      ) : view === 'table' ? (
        <TableContainer
          bg="white"
          borderWidth="1px"
          borderColor={companyBorder}
          borderRadius="28px"
          overflow="hidden"
          boxShadow="0 18px 44px rgba(15, 23, 42, 0.05)"
        >
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th py={4}>Company</Th>
                <Th py={4}>Profile</Th>
                <Th py={4}>Primary contact</Th>
                <Th py={4}>Coverage</Th>
                <Th py={4} textAlign="right">
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredCompanies.map((company) => (
                <CompanyTableRow
                  key={company.id}
                  company={company}
                  onOpen={openCompany}
                  onEdit={editCompanyRecord}
                  onDelete={confirmDelete}
                />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {filteredCompanies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              onOpen={openCompany}
              onEdit={editCompanyRecord}
              onDelete={confirmDelete}
            />
          ))}
        </SimpleGrid>
      )}

      {!loading && companies.length > 0 ? (
        <Stack mt={5} spacing={2}>
          <Text fontSize="sm" color="gray.500">
            Tip: use `/` to jump straight into company search from this page.
          </Text>
          <HStack spacing={4} color="gray.500" fontSize="sm" flexWrap="wrap">
            <HStack spacing={2}>
              <Box as={Building2 as ElementType} boxSize={4} />
              <Text>Prioritize missing website records to improve recognition across the CRM.</Text>
            </HStack>
            <HStack spacing={2}>
              <Box as={Users as ElementType} boxSize={4} />
              <Text>Complete profiles convert faster because the next action is obvious.</Text>
            </HStack>
          </HStack>
        </Stack>
      ) : null}

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete company?</ModalHeader>
          <ModalBody>
            <Text>
              You are about to delete{' '}
              <Badge colorScheme="red">{deleteTarget?.name}</Badge>. This action
              cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              isLoading={deleting}
              loadingText="Deleting"
            >
              Delete company
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </CompanyShell>
  );
}
